/**
 * SanctuarySkillEffect — Specter Veil/Distortion terminal passive.
 *
 * Concealment replenishes health at 2% max HP per second. The regen hook
 * on Player already gates on concealment state (hide or stealth active)
 * and dead checks — this class simply sets the rate.
 *
 * On equip: sets `concealedHealthRegenRate` on Player to 0.02.
 * On unequip: resets `concealedHealthRegenRate` to 0 (default).
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-080 (terminal passive pattern)
 * @see specter-drift-path-proposal.md § Sanctuary
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface SanctuaryDeps {
  /** Fraction of max HP regenerated per second while concealed. Default 0. */
  concealedHealthRegenRate: number;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface SanctuaryConfig {
  /** Health regen rate while concealed (fraction of max HP per second). */
  readonly concealedHealthRegenRate: number;
}

const DEFAULT_CONFIG: SanctuaryConfig = {
  concealedHealthRegenRate: 0.02,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class SanctuarySkillEffect implements SkillEffect {
  readonly skillId = 'sanctuary';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;
  readonly staminaCost: number = 0;

  private readonly deps: SanctuaryDeps;
  private readonly config: SanctuaryConfig;

  /** Whether the rate has been applied (guards against double-application). */
  private _applied = false;

  constructor(deps: SanctuaryDeps, config: Partial<SanctuaryConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- SkillEffect interface ---

  canActivate(): boolean { return false; }
  activate(): boolean { return false; }
  deactivate(): void {}
  isActive(): boolean { return false; }

  update(_delta: number): void {
    if (!this._applied) {
      this.deps.concealedHealthRegenRate = this.config.concealedHealthRegenRate;
      this._applied = true;
    }
  }

  destroy(): void {
    if (this._applied) {
      this.deps.concealedHealthRegenRate = 0;
      this._applied = false;
    }
  }
}
